// Create or edit a DE subscription (coverage rows 62, 63), shared by Setup › DE subscriptions and
// the DE profile's Subscriptions tab. A subscription is the monthly agreement; the scheduler posts
// one cost row per month from it, and a change applies to months not yet posted. The amount is
// typed in the company's reporting currency (the API refuses any other). Agency-visibility rows
// are offered to agency operators only: nobody else would ever see them.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ComponentType, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { DeSubscription } from "../../api/types";
import { fractionDigits } from "../format";
import { companyQuery, keys } from "../queries";
import { problemText } from "./de-status";

/** A DE that can carry a subscription: it has a seat. */
export type SubscribableDe = { id: string; name: string; seatId: string };

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

function toMinor(text: string, currency: string): number | null {
  const n = Number(text);
  if (text.trim() === "" || !Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 10 ** fractionDigits(currency));
}

const fromMinor = (minor: number, currency: string) =>
  String(minor / 10 ** fractionDigits(currency));

/** This month in UTC (`2026-10`). */
const thisMonth = () => new Date().toISOString().slice(0, 7);

export function SubscriptionDialog({
  api,
  companyId,
  operator,
  des,
  existing,
  trigger,
}: {
  api: Api;
  companyId: string;
  operator: boolean;
  /** Create: the DEs to choose from (one entry = that DE, no picker). */
  des?: SubscribableDe[];
  /** Edit: the subscription to change. */
  existing?: DeSubscription;
  /** The opening button's label and accessible name. */
  trigger: {
    label: string;
    name?: string;
    variant?: "default" | "secondary" | "ghost";
    /** `sm` in a page header; `icon-sm` shows `icon` only, the label as its tooltip. */
    size?: "default" | "sm" | "icon-sm";
    icon?: ComponentType<{ "aria-hidden"?: boolean; "data-icon"?: string }>;
    className?: string;
  };
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const Icon = trigger.icon;
  const openDialog = () => {
    setForm(blank());
    save.reset();
    setOpen(true);
  };
  const blank = () => ({
    de: des?.[0]?.id ?? "",
    amount: existing ? fromMinor(existing.amount_minor, existing.currency) : "",
    start: existing?.start_month ?? thisMonth(),
    end: existing?.end_month ?? "",
    scope: (existing?.scope ?? "client") as DeSubscription["scope"],
  });
  const [form, setForm] = useState(blank);
  const set = (patch: Partial<ReturnType<typeof blank>>) => setForm((f) => ({ ...f, ...patch }));
  const ids = { de: useId(), amount: useId(), start: useId(), end: useId(), scope: useId() };
  // A new subscription is in the reporting currency, read when the dialog opens (no load cost).
  const detail = useQuery({ ...companyQuery(api, companyId), enabled: open && !existing });
  const cur = existing?.currency ?? detail.data?.reporting_currency ?? "";
  const minor = cur ? toMinor(form.amount, cur) : null;
  const picked = des?.find((d) => d.id === form.de);
  const endOk = !form.end || (MONTH.test(form.end) && form.end >= form.start);
  const changed =
    !existing ||
    minor !== existing.amount_minor ||
    (form.end || null) !== existing.end_month ||
    form.scope !== existing.scope;
  const valid =
    minor !== null &&
    endOk &&
    changed &&
    (existing ? true : Boolean(picked) && MONTH.test(form.start));

  const save = useMutation({
    mutationFn: async () => {
      if (minor === null) throw new Error("amount");
      if (existing)
        return api.client.deSubscriptions.update({
          params: { co: companyId, id: existing.id },
          body: {
            ...(minor !== existing.amount_minor ? { amount_minor: minor } : {}),
            ...((form.end || null) !== existing.end_month ? { end_month: form.end || null } : {}),
            ...(form.scope !== existing.scope ? { scope: form.scope } : {}),
          },
        });
      if (!picked || !cur) throw new Error("de");
      return api.client.deSubscriptions.create({
        params: { co: companyId },
        idempotencyKey: newIdempotencyKey(),
        body: {
          seat_id: picked.seatId,
          membership_id: picked.id,
          amount_minor: minor,
          currency: cur,
          start_month: form.start,
          end_month: form.end || null,
          scope: form.scope,
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.subscriptions(companyId) });
      setOpen(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (save.isPending) return;
        setOpen(next);
        if (next) {
          setForm(blank());
          save.reset();
        }
      }}
    >
      {trigger.size === "icon-sm" && Icon ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant={trigger.variant ?? "ghost"}
                size="icon-sm"
                aria-label={trigger.name ?? trigger.label}
                className={trigger.className}
                onClick={openDialog}
              />
            }
          >
            <Icon aria-hidden={true} />
          </TooltipTrigger>
          <TooltipContent>{trigger.label}</TooltipContent>
        </Tooltip>
      ) : (
        <Button
          variant={trigger.variant ?? "default"}
          size={trigger.size ?? (trigger.variant === "ghost" ? "sm" : "default")}
          aria-label={trigger.name}
          className={trigger.className}
          onClick={openDialog}
        >
          {Icon ? <Icon data-icon="inline-start" /> : null}
          {trigger.label}
        </Button>
      )}
      <DialogContent className="sm:max-w-lg">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{existing ? "Edit subscription" : "New DE subscription"}</DialogTitle>
            <DialogDescription>
              {existing
                ? "Changes apply to months not posted yet. Posted months stay as they are."
                : "The monthly fee for a DE. One cost row is posted per month from the start month."}
            </DialogDescription>
          </DialogHeader>
          {!existing && des && des.length > 1 ? (
            <Field>
              <FieldLabel htmlFor={ids.de}>Digital employee</FieldLabel>
              <NativeSelect
                id={ids.de}
                value={form.de}
                onChange={(e) => set({ de: e.target.value })}
              >
                {des.map((d) => (
                  <NativeSelectOption key={d.id} value={d.id}>
                    {d.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          ) : null}
          <Field>
            <FieldLabel htmlFor={ids.amount}>Monthly amount{cur ? ` (${cur})` : ""}</FieldLabel>
            <Input
              id={ids.amount}
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              required
              value={form.amount}
              onChange={(e) => set({ amount: e.target.value })}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor={ids.start}>Start month</FieldLabel>
              <Input
                id={ids.start}
                type="month"
                required={!existing}
                disabled={Boolean(existing)}
                value={form.start}
                onChange={(e) => set({ start: e.target.value })}
              />
              {existing ? <FieldDescription>Fixed once created.</FieldDescription> : null}
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.end}>End month</FieldLabel>
              <Input
                id={ids.end}
                type="month"
                min={form.start}
                value={form.end}
                aria-invalid={endOk ? undefined : true}
                onChange={(e) => set({ end: e.target.value })}
              />
              <FieldDescription>
                {endOk ? "Empty: runs until ended." : "The end month is before the start month."}
              </FieldDescription>
            </Field>
          </div>
          {operator ? (
            <Field>
              <FieldLabel htmlFor={ids.scope}>Visible to</FieldLabel>
              <NativeSelect
                id={ids.scope}
                value={form.scope}
                onChange={(e) => set({ scope: e.target.value as DeSubscription["scope"] })}
              >
                <NativeSelectOption value="client">Client and agency</NativeSelectOption>
                <NativeSelectOption value="agency">Agency only</NativeSelectOption>
              </NativeSelect>
            </Field>
          ) : null}
          {save.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {problemText(save.error, "The subscription could not be saved.")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose
              disabled={save.isPending}
              render={<Button variant="secondary" type="button" />}
            >
              Cancel
            </DialogClose>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? "Saving…" : existing ? "Save changes" : "Create subscription"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
